import { useState, useEffect, useCallback, useMemo } from "react";
import { useAuth } from "../context/AuthContext";
import {
  BarChart3, Package, CheckCircle, AlertTriangle, Truck, MapPin,
  FileCheck, ArrowUpRight, Activity, Building2
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell,
  Area, AreaChart, Legend, CartesianGrid
} from "recharts";
import api from "../lib/api";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const TEAL = "#0D9488";
const BLUE = "#3B82F6";
const PURPLE = "#8B5CF6";
const AMBER = "#F59E0B";
const RED = "#EF4444";
const GREEN = "#10B981";
const SLATE = "#64748B";

const STATUS_ORDER = [
  { key: "NEW", label: "New", color: SLATE },
  { key: "AGENT", label: "Assigned", color: "#0891B2" },
  { key: "SCHEDULED", label: "Scheduled", color: "#7C3AED" },
  { key: "IN_TRANSIT", label: "In Transit", color: AMBER },
  { key: "JHB_DEPOT", label: "JHB Depot", color: "#6366F1" },
  { key: "DELIVERED", label: "Delivered", color: "#06B6D4" },
  { key: "COMPLETED", label: "Completed", color: GREEN },
];

const RANGE_OPTIONS = [
  { key: "today", label: "Today" },
  { key: "week", label: "7 Days" },
  { key: "month", label: "30 Days" },
  { key: "90d", label: "90 Days" },
  { key: "ytd", label: "Year" },
  { key: "all", label: "All Time" },
];

function cls(...a) { return a.filter(Boolean).join(" "); }

function KPICard({ icon: Icon, label, value, accent = "teal", subtext, testId }) {
  const accentMap = {
    teal: "bg-teal-50 text-teal-600 border-teal-100",
    blue: "bg-blue-50 text-blue-600 border-blue-100",
    amber: "bg-amber-50 text-amber-600 border-amber-100",
    red: "bg-red-50 text-red-600 border-red-100",
    green: "bg-emerald-50 text-emerald-600 border-emerald-100",
    purple: "bg-purple-50 text-purple-600 border-purple-100",
    sky: "bg-sky-50 text-sky-600 border-sky-100",
    slate: "bg-slate-50 text-slate-600 border-slate-100",
  };
  const valueStr = String(value);
  // Pick a font-size that fits the card width.
  // - Numeric / short tokens (≤4 chars) → can go big (up to 1.875rem)
  // - Longer single tokens (e.g. "Gauteng", "KwaZulu-Natal") → shrink relative to
  //   the card's own width via container query units (cqi). This guarantees the
  //   text fits the card even when the grid layout makes it narrow.
  const isShort = valueStr.length <= 4;
  const valueSizeClass = isShort
    ? "[font-size:clamp(1.25rem,12cqi,1.875rem)]"
    : "[font-size:clamp(0.875rem,9cqi,1.5rem)]";
  return (
    <div
      className="bg-white rounded-xl border border-slate-200 p-3 sm:p-4 hover:shadow-sm transition-shadow min-w-0 [container-type:inline-size]"
      data-testid={testId}
    >
      <div className="flex items-start justify-between gap-2 min-w-0">
        <div className="min-w-0 flex-1">
          <p className="text-[10px] uppercase tracking-[0.12em] text-slate-400 font-medium leading-tight break-words">{label}</p>
          <p
            className={cls(
              "font-bold text-slate-900 mt-1 leading-tight whitespace-nowrap overflow-hidden text-ellipsis",
              valueSizeClass,
            )}
            title={valueStr}
          >
            {value}
          </p>
          {subtext && <p className="text-[11px] text-slate-500 mt-1.5 break-words leading-snug">{subtext}</p>}
        </div>
        <div className={cls("shrink-0 w-8 h-8 sm:w-10 sm:h-10 rounded-xl flex items-center justify-center border", accentMap[accent])}>
          <Icon size={16} />
        </div>
      </div>
    </div>
  );
}

function SectionCard({ title, subtitle, controls, children, testId }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid={testId}>
      <div className="flex flex-wrap items-center gap-2 px-4 py-3 border-b border-slate-100">
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-slate-800">{title}</h3>
          {subtitle && <p className="text-[11px] text-slate-500 mt-0.5 truncate">{subtitle}</p>}
        </div>
        {controls && <div className="flex items-center gap-1.5 flex-wrap">{controls}</div>}
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}

function MiniSelect({ value, onChange, options, testId, title }) {
  return (
    <select
      data-testid={testId}
      title={title}
      value={value || ""}
      onChange={(e) => onChange(e.target.value || null)}
      className="h-7 text-[11px] border border-slate-200 rounded-md px-2 bg-white text-slate-700 hover:border-teal-400 focus:border-teal-500 focus:ring-1 focus:ring-teal-500 outline-none"
    >
      {options.map((o) => <option key={String(o.value)} value={o.value}>{o.label}</option>)}
    </select>
  );
}

function formatHours(h) {
  if (h == null) return "—";
  if (h < 24) return `${h.toFixed(1)} h`;
  const d = Math.floor(h / 24);
  const r = Math.round(h - d * 24);
  return `${d}d ${r}h`;
}

function fmtShort(d) {
  try { return new Date(d).toLocaleDateString("en-ZA", { day: "2-digit", month: "short" }); } catch { return d; }
}

export default function ManagementDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState("month");
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState(() => {
    try { return localStorage.getItem("cargodash_dashboard_company") || "all"; } catch { return "all"; }
  });
  const [coverage, setCoverage] = useState(() => {
    try { return localStorage.getItem("cargodash_dashboard_coverage") || "all"; } catch { return "all"; }
  });
  const [regions, setRegions] = useState([]);
  const [region, setRegion] = useState(() => {
    try { return localStorage.getItem("cargodash_dashboard_region") || "all"; } catch { return "all"; }
  });

  const [filters, setFilters] = useState({ areas: [], stores: [], agents: [] });

  // --- Interactive Mix chart state ---
  const [mixScope, setMixScope] = useState("active"); // active | completed
  const [mixArea, setMixArea] = useState(null);
  const [mixStore, setMixStore] = useState(null);
  const [mixData, setMixData] = useState(null);

  // --- Interactive Top Performers state ---
  const [topMetric, setTopMetric] = useState("stores"); // stores | areas | agents | types | status
  const [topScope, setTopScope] = useState("active");   // active | completed
  const [topFilter, setTopFilter] = useState(null);     // area-filter
  const [topData, setTopData] = useState(null);

  const rangeParams = useMemo(() => {
    const p = range && range !== "all" ? { range } : {};
    if (companyId && companyId !== "all") p.company_id = companyId;
    if (coverage && coverage !== "all") p.coverage = coverage;
    if (region && region !== "all") p.region = region;
    return p;
  }, [range, companyId, coverage, region]);

  useEffect(() => {
    try { localStorage.setItem("cargodash_dashboard_company", companyId); } catch { /* no-op */ }
  }, [companyId]);

  useEffect(() => {
    try { localStorage.setItem("cargodash_dashboard_coverage", coverage); } catch { /* no-op */ }
  }, [coverage]);

  useEffect(() => {
    try { localStorage.setItem("cargodash_dashboard_region", region); } catch { /* no-op */ }
  }, [region]);

  const loadStats = useCallback(async () => {
    try {
      const { data } = await api.get("/dashboard/stats", { params: rangeParams });
      setStats(data);
    } catch (e) { toast.error("Failed to load dashboard"); }
    finally { setLoading(false); }
  }, [rangeParams]);

  const loadFilters = useCallback(async () => {
    try {
      const { data } = await api.get("/dashboard/filters");
      setFilters(data);
    } catch { /* ignore */ }
  }, []);

  const loadCompanies = useCallback(async () => {
    try {
      const { data } = await api.get("/companies");
      setCompanies(data || []);
    } catch { /* ignore: SS/SM get 403, which is fine — no filter shown */ }
  }, []);

  const loadRegions = useCallback(async () => {
    try {
      const { data } = await api.get("/regions");
      setRegions((data || []).map((r) => (typeof r === "string" ? r : r.name)));
    } catch { /* ignore */ }
  }, []);

  const loadMix = useCallback(async () => {
    try {
      const { data } = await api.get("/dashboard/mix", { params: {
        ...rangeParams, scope: mixScope, area: mixArea || undefined, store: mixStore || undefined,
      }});
      setMixData(data);
    } catch { setMixData({ DELIVERY: 0, COLLECTION: 0, UNSET: 0 }); }
  }, [rangeParams, mixScope, mixArea, mixStore]);

  const loadTop = useCallback(async () => {
    try {
      const params = { ...rangeParams, metric: topMetric, status_scope: topScope, limit: 10 };
      if (topFilter && topMetric !== "areas") params.area = topFilter;
      const { data } = await api.get("/dashboard/top", { params });
      setTopData(data);
    } catch { setTopData({ metric: topMetric, rows: [] }); }
  }, [rangeParams, topMetric, topScope, topFilter]);

  useEffect(() => { loadStats(); loadFilters(); loadCompanies(); loadRegions(); }, [loadStats, loadFilters, loadCompanies, loadRegions]);
  useEffect(() => { loadMix(); }, [loadMix]);
  useEffect(() => { loadTop(); }, [loadTop]);
  useEffect(() => { const i = setInterval(loadStats, 60000); return () => clearInterval(i); }, [loadStats]);

  if (loading || !stats) return <div className="p-8 text-sm text-slate-400">Loading dashboard…</div>;
  const s = stats;
  const slaTotal = (s.sla?.on_track || 0) + (s.sla?.warning || 0) + (s.sla?.late || 0);
  const jt = mixData || { DELIVERY: 0, COLLECTION: 0, UNSET: 0 };

  const statusBarData = STATUS_ORDER.map(o => ({
    label: o.label, count: s.by_status?.[o.key] || 0, color: o.color,
  }));

  const jobTypePie = [
    { name: "Delivery", value: jt.DELIVERY || 0, color: BLUE },
    { name: "Collection", value: jt.COLLECTION || 0, color: PURPLE },
    ...((jt.UNSET || 0) > 0 ? [{ name: "Not set", value: jt.UNSET, color: SLATE }] : []),
  ];

  const slaPie = [
    { name: "On Track", value: s.sla?.on_track || 0, color: GREEN },
    { name: "At Risk", value: s.sla?.warning || 0, color: AMBER },
    { name: "Overdue", value: s.sla?.late || 0, color: RED },
  ];

  const throughput = (s.throughput || s.throughput_14d || []).map(d => ({ ...d, label: fmtShort(d.date) }));

  const topRows = topData?.rows || [];
  const topMax = topRows.reduce((m, r) => Math.max(m, r.count), 1);

  const metricOptions = [
    { value: "stores", label: "Top Stores" },
    { value: "areas", label: "Top Areas" },
    { value: "types", label: "By Job Type" },
    { value: "status", label: "By Status" },
  ];
  if (user?.role !== "SS" && user?.role !== "SM") {
    metricOptions.splice(2, 0, { value: "agents", label: "Top 3PA Agents" });
  }

  return (
    <div className="p-4 lg:p-6 max-w-full space-y-5">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-teal-50 border border-teal-100 flex items-center justify-center">
            <BarChart3 size={20} className="text-teal-600" />
          </div>
          <div>
            <h1 className="text-xl lg:text-2xl font-bold text-slate-900" data-testid="page-title">Dashboard</h1>
            <p className="text-xs text-slate-500">
              {user?.role === "SM" ? "Skyworth operational overview" : "Workflow performance and service levels"}
            </p>
          </div>
        </div>

        {/* Global filters: company + date range */}
        <div className="flex items-center gap-2 flex-wrap">
          {companies.length > 0 && user?.role !== "SS" && (
            <Select value={companyId} onValueChange={setCompanyId}>
              <SelectTrigger data-testid="dashboard-company-select" className="h-9 bg-white border-slate-200 text-xs w-56">
                <div className="flex items-center gap-1.5 truncate">
                  <Building2 size={12} className="text-teal-600 shrink-0" />
                  <SelectValue />
                </div>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All companies</SelectItem>
                {companies.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          {/* Coverage filter pill */}
          <div className="inline-flex items-center bg-white rounded-xl border border-slate-200 p-0.5" data-testid="dashboard-coverage">
            {[
              { key: "all", label: "All" },
              { key: "LOCAL", label: "Local" },
              { key: "NATIONAL", label: "National" },
            ].map((o) => (
              <button
                key={o.key}
                data-testid={`coverage-${o.key}`}
                onClick={() => setCoverage(o.key)}
                className={cls(
                  "px-3 h-8 text-[11px] font-medium rounded-lg transition-colors",
                  coverage === o.key ? "bg-teal-600 text-white shadow-sm" : "text-slate-600 hover:bg-slate-50"
                )}
              >
                {o.label}
              </button>
            ))}
          </div>

          {/* Region filter dropdown */}
          {regions.length > 0 && (
            <Select value={region} onValueChange={setRegion}>
              <SelectTrigger data-testid="dashboard-region-select" className="h-9 bg-white border-slate-200 text-xs w-44">
                <div className="flex items-center gap-1.5 truncate">
                  <MapPin size={12} className="text-amber-600 shrink-0" />
                  <SelectValue />
                </div>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All regions</SelectItem>
                {regions.map((r) => (<SelectItem key={r} value={r}>{r}</SelectItem>))}
              </SelectContent>
            </Select>
          )}

          <div className="inline-flex items-center bg-white rounded-xl border border-slate-200 p-0.5" data-testid="dashboard-range">
            {RANGE_OPTIONS.map((o) => (
              <button
                key={o.key}
                data-testid={`range-${o.key}`}
                onClick={() => setRange(o.key)}
                className={cls(
                  "px-3 h-8 text-[11px] font-medium rounded-lg transition-colors",
                  range === o.key ? "bg-teal-600 text-white shadow-sm" : "text-slate-600 hover:bg-slate-50"
                )}
              >
                {o.label}
              </button>
            ))}
            <button
              onClick={loadStats}
              className="ml-1 px-2 h-8 text-[11px] text-slate-500 hover:text-teal-600"
              data-testid="dashboard-refresh"
              title="Refresh"
            >
              ↻
            </button>
          </div>
        </div>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 2xl:grid-cols-8 gap-3">
        <KPICard testId="kpi-active" icon={Activity} label="Active Jobs" value={s.active_jobs} accent="teal" subtext={`${s.total_jobs} total in range`} />
        <KPICard testId="kpi-completed" icon={CheckCircle} label="Completed" value={s.completed_jobs} accent="green" subtext={`${s.completion_rate}% completion rate`} />
        <KPICard testId="kpi-delivery" icon={Truck} label="Deliveries (active)" value={s.job_type_active?.DELIVERY || 0} accent="blue" subtext={s.avg_lead_delivery_hours != null ? `Avg ${formatHours(s.avg_lead_delivery_hours)} lead` : ""} />
        <KPICard testId="kpi-collection" icon={Package} label="Collections (active)" value={s.job_type_active?.COLLECTION || 0} accent="purple" subtext={s.avg_lead_collection_hours != null ? `Avg ${formatHours(s.avg_lead_collection_hours)} lead` : ""} />
        <KPICard
          testId="kpi-coverage"
          icon={MapPin}
          label="Local / National"
          value={`${s.coverage_breakdown?.LOCAL || 0} / ${s.coverage_breakdown?.NATIONAL || 0}`}
          accent="sky"
          subtext={s.coverage_breakdown?.UNSET ? `${s.coverage_breakdown.UNSET} unset` : "in selected range"}
        />
        {(() => {
          const rb = s.region_breakdown || {};
          const tops = Object.entries(rb)
            .filter(([k]) => k !== "Unassigned")
            .sort((a, b) => b[1] - a[1])
            .filter(([, v]) => v > 0);
          const [top, ...rest] = tops;
          return (
            <KPICard
              testId="kpi-region"
              icon={MapPin}
              label="Top region"
              value={top ? top[0] : "—"}
              accent="amber"
              subtext={top
                ? `${top[1]} JCs${rest.length ? ` · next: ${rest.slice(0, 2).map(([n, v]) => `${n} ${v}`).join(", ")}` : ""}`
                : (rb.Unassigned ? `${rb.Unassigned} unassigned` : "no jobs in range")}
            />
          );
        })()}
        <KPICard testId="kpi-sla" icon={AlertTriangle} label="SLA at risk / late" value={`${s.sla?.warning || 0} / ${s.sla?.late || 0}`} accent={(s.sla?.late || 0) > 0 ? "red" : "amber"} subtext={slaTotal ? `of ${slaTotal} active tracked` : ""} />
        <KPICard testId="kpi-approvals" icon={FileCheck} label="Surcharge pending" value={s.approvals?.pending || 0} accent={s.approvals?.pending > 0 ? "amber" : "slate"} subtext={`${s.approvals?.approved || 0} approved · ${s.approvals?.rejected || 0} rejected`} />
      </div>

      {/* Pipeline */}
      <SectionCard testId="section-funnel" title="Workflow Pipeline" subtitle="Live count at each lifecycle stage for the selected period">
        <ResponsiveContainer width="100%" height={230}>
          <BarChart data={statusBarData} barCategoryGap={12}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 11, fill: SLATE }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 11, fill: SLATE }} axisLine={false} tickLine={false} />
            <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 12 }} />
            <Bar dataKey="count" radius={[6, 6, 0, 0]}>
              {statusBarData.map((d, i) => <Cell key={i} fill={d.color} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </SectionCard>

      {/* Throughput + Mix + SLA */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2">
          <SectionCard
            testId="section-throughput"
            title="Completed Jobs Trend"
            subtitle={s.avg_lead_hours != null ? `Avg lead time: ${formatHours(s.avg_lead_hours)}` : "Throughput split by job type"}
          >
            <ResponsiveContainer width="100%" height={230}>
              <AreaChart data={throughput}>
                <defs>
                  <linearGradient id="gDeliv" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={BLUE} stopOpacity={0.35} />
                    <stop offset="100%" stopColor={BLUE} stopOpacity={0.02} />
                  </linearGradient>
                  <linearGradient id="gColl" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={PURPLE} stopOpacity={0.35} />
                    <stop offset="100%" stopColor={PURPLE} stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 10, fill: SLATE }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 10, fill: SLATE }} axisLine={false} tickLine={false} allowDecimals={false} />
                <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} iconType="circle" />
                <Area type="monotone" name="Delivery" dataKey="delivery" stackId="a" stroke={BLUE} fill="url(#gDeliv)" strokeWidth={2} />
                <Area type="monotone" name="Collection" dataKey="collection" stackId="a" stroke={PURPLE} fill="url(#gColl)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </SectionCard>
        </div>

        <div className="space-y-5">
          <SectionCard
            testId="section-mix"
            title="Delivery vs Collection"
            subtitle={`${mixScope === "active" ? "Active" : "Completed"}${mixArea ? ` · ${mixArea}` : ""}${mixStore ? ` · ${mixStore}` : ""}`}
            controls={
              <>
                <MiniSelect
                  testId="mix-scope"
                  value={mixScope}
                  onChange={(v) => setMixScope(v || "active")}
                  options={[{ value: "active", label: "Active" }, { value: "completed", label: "Completed" }]}
                />
                <MiniSelect
                  testId="mix-area"
                  value={mixArea || ""}
                  onChange={setMixArea}
                  options={[{ value: "", label: "All areas" }, ...filters.areas.map(a => ({ value: a, label: a }))]}
                />
                <MiniSelect
                  testId="mix-store"
                  value={mixStore || ""}
                  onChange={setMixStore}
                  options={[{ value: "", label: "All stores" }, ...filters.stores.map(a => ({ value: a, label: a }))]}
                />
              </>
            }
          >
            {jobTypePie.every(x => !x.value) ? (
              <p className="text-xs text-slate-400 text-center py-6">No jobs match these filters</p>
            ) : (
              <ResponsiveContainer width="100%" height={160}>
                <PieChart>
                  <Pie data={jobTypePie} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={45} outerRadius={65} paddingAngle={2}>
                    {jobTypePie.map((d, i) => <Cell key={i} fill={d.color} />)}
                  </Pie>
                  <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            )}
            <div className="grid grid-cols-3 gap-2 text-[11px] -mt-2">
              {jobTypePie.map(d => (
                <div key={d.name} className="flex items-center gap-1.5">
                  <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: d.color }} />
                  <span className="text-slate-600">{d.name}</span>
                  <span className="text-slate-900 font-semibold ml-auto">{d.value}</span>
                </div>
              ))}
            </div>
          </SectionCard>

          <SectionCard testId="section-sla" title="SLA Health" subtitle={`${slaTotal} active jobs tracked`}>
            {slaTotal === 0 ? (
              <p className="text-xs text-slate-400 text-center py-6">No active jobs tracked</p>
            ) : (
              <ResponsiveContainer width="100%" height={160}>
                <PieChart>
                  <Pie data={slaPie} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={45} outerRadius={65} paddingAngle={2}>
                    {slaPie.map((d, i) => <Cell key={i} fill={d.color} />)}
                  </Pie>
                  <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            )}
            <div className="grid grid-cols-3 gap-2 text-[11px] -mt-2">
              {slaPie.map(d => (
                <div key={d.name} className="flex items-center gap-1.5">
                  <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: d.color }} />
                  <span className="text-slate-600">{d.name}</span>
                  <span className="text-slate-900 font-semibold ml-auto">{d.value}</span>
                </div>
              ))}
            </div>
          </SectionCard>
        </div>
      </div>

      {/* Unified Top Performers */}
      <SectionCard
        testId="section-top"
        title="Top Performers"
        subtitle={`${topScope === "active" ? "Active" : "Completed"}${topFilter ? ` · ${topFilter}` : ""} · ${metricOptions.find(m => m.value === topMetric)?.label || topMetric}`}
        controls={
          <>
            <MiniSelect testId="top-metric" value={topMetric} onChange={(v) => setTopMetric(v || "stores")} options={metricOptions} />
            <MiniSelect
              testId="top-scope"
              value={topScope}
              onChange={(v) => setTopScope(v || "active")}
              options={[{ value: "active", label: "Active" }, { value: "completed", label: "Completed" }, { value: "all", label: "All" }]}
            />
            {topMetric !== "areas" && (
              <MiniSelect
                testId="top-area-filter"
                value={topFilter || ""}
                onChange={setTopFilter}
                options={[{ value: "", label: "All areas" }, ...filters.areas.map(a => ({ value: a, label: a }))]}
              />
            )}
          </>
        }
      >
        {topRows.length === 0 ? (
          <p className="text-xs text-slate-400 text-center py-6">No data for this selection</p>
        ) : (
          <div className="space-y-2">
            {topRows.map((r, idx) => {
              const pct = (r.count / topMax) * 100;
              return (
                <div key={r.label} className="flex items-center gap-3">
                  <span className="text-[10px] text-slate-400 font-mono w-5">{idx + 1}</span>
                  {topMetric === "stores" && <MapPin size={12} className="text-teal-500 flex-shrink-0" />}
                  {topMetric === "areas" && <MapPin size={12} className="text-blue-500 flex-shrink-0" />}
                  {topMetric === "agents" && <Truck size={12} className="text-teal-500 flex-shrink-0" />}
                  {topMetric === "types" && <Package size={12} className="text-purple-500 flex-shrink-0" />}
                  {topMetric === "status" && <Activity size={12} className="text-amber-500 flex-shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-xs text-slate-700 truncate">{r.label}</p>
                      <p className="text-xs font-semibold text-slate-900">{r.count}</p>
                    </div>
                    <div className="h-1.5 bg-slate-100 rounded-full mt-1 overflow-hidden">
                      <div className="h-full bg-teal-500" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </SectionCard>

      {/* Accounting pipeline (CM only) */}
      {user?.role === "CM" && (
        <SectionCard testId="section-accounting" title="Accounting Pipeline" subtitle="Completed JCs at each accountant stage">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {["New", "In-house Query", "CargoDash Query", "Credit Note", "Captured"].map((label, i) => {
              const count = s.accounting?.[label] || 0;
              const colors = [
                "bg-slate-50 border-slate-200 text-slate-700",
                "bg-amber-50 border-amber-200 text-amber-700",
                "bg-red-50 border-red-200 text-red-700",
                "bg-purple-50 border-purple-200 text-purple-700",
                "bg-emerald-50 border-emerald-200 text-emerald-700",
              ];
              return (
                <div key={label} className={cls("rounded-xl border p-3", colors[i])}>
                  <p className="text-[10px] uppercase tracking-wide font-medium opacity-70">{label}</p>
                  <p className="text-2xl font-bold mt-1">{count}</p>
                </div>
              );
            })}
          </div>
        </SectionCard>
      )}
    </div>
  );
}
